import { useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { batch } from 'react-redux';
import isEmpty from 'lodash/isEmpty';
import { useMutation } from '@apollo/client';
import ExecuteReportAction from '../../../../../components/Builder/ExecuteReport/ExecuteReportAction';
import validateSignificanceBuilder from '../utils/validateSignificanceBuilder';
import { useAppDispatch, useAppSelector } from '../../../../../redux/hooks';
import { builderSelector } from '../../../../../redux/selectors/builder';
import {
  setErrorAction,
  setSignificanceErrors,
} from '../../../../../redux/slices/oldBuilder';
import { SIGNIFICANCE_EXECUTE } from '../../../../../utils/apollo/significance/mutations';
import {
  AnalysisStatus,
  ISignificanceExecuteMutation,
  ISignificanceExecuteMutationVariables,
} from '../../../../../utils/apollo/graphqlSchema';
import prepareSignificanceInput from '../utils/prepareSignificanceInput';
import printGraphqlErrors from '../../../../../utils/apollo/printGraphqlErrors';
import { assertCondition } from '../../../../../utils/types';
import toastMessage from '../../../../../utils/toastMessage';
import { analysisSelector } from '../../../../../redux/selectors/analyses';
import { reportCreated } from '../../../../../utils/analytics/events/report/events';
import { useAnalytics } from '../../../../../utils/analytics/useAnalytics';
import { formErrorViewed } from '../../../../../utils/analytics/events/error/events';
import { countBuilderErrors } from '../../../../../components/Builder/utils/errorUtil';
import { AnalyticsErrorContext } from '../../../../../utils/analytics/events/error/types';
import useCachedEntitiesSelector from '../../../../../redux/selectorHooks/useCachedEntitiesSelector';
import useExecutedReport from '../../../../../utils/builder/useExecutedReport';
import { IS_SCREENSHOT_MODE } from '../../../../../config/constants';

const SignificanceExecute = () => {
  const dispatch = useAppDispatch();
  const { track } = useAnalytics();

  const urlParams = useParams();
  const builderId = urlParams?.builderId;

  const handleExecutedReport = useExecutedReport();

  const analysisMemoizedSelector = useMemo(analysisSelector, []);
  const analysis = useAppSelector((state) =>
    analysisMemoizedSelector(state, builderId),
  );
  const display = analysis?.metadata?.display;
  const description = analysis?.metadata?.description;

  const builderState = useAppSelector(builderSelector);
  const replaceReport = useCachedEntitiesSelector('replaceReport');

  const handleCompleteExecution = (response: ISignificanceExecuteMutation) => {
    const { significanceExecute } = response;
    const { entity, errors } = significanceExecute;

    if (printGraphqlErrors(errors)) {
      return;
    }

    assertCondition(entity?.__typename === 'Significance');

    if (entity.status === AnalysisStatus.Failed) {
      toastMessage({
        message: 'Execution Failed.',
        type: 'error',
      });
    }

    handleExecutedReport(entity);
    track(reportCreated(entity));
  };

  const [execute, { loading }] = useMutation<
    ISignificanceExecuteMutation,
    ISignificanceExecuteMutationVariables
  >(SIGNIFICANCE_EXECUTE, {
    onCompleted: handleCompleteExecution,
  });

  const { builderErrors, significanceErrors } =
    validateSignificanceBuilder(builderState);

  const noBuilderErrors = isEmpty(builderErrors);
  const noSignificanceErrors = isEmpty(significanceErrors);

  const builderIsValid = noBuilderErrors && noSignificanceErrors;

  const handleExecute = async () => {
    // Only when builder is valid we will prepare input and eE,execute
    if (builderIsValid) {
      await execute({
        variables: {
          input: prepareSignificanceInput({
            builderState,
            replaceReport,
            analysisName: display,
            analysisDescription: description,
          }),
        },
      });
      return;
    }

    const totalErrors =
      countBuilderErrors(builderErrors) +
      countBuilderErrors(significanceErrors);
    track(
      formErrorViewed({
        message: `Failed to execute report. ${totalErrors} errors found.`,
        context: AnalyticsErrorContext.ExecuteReport,
      }),
    );

    batch(() => {
      if (!noBuilderErrors) {
        dispatch(setErrorAction(builderErrors));
      }
      if (!noSignificanceErrors) {
        dispatch(setSignificanceErrors({ errors: significanceErrors }));
      }
    });
  };

  if (IS_SCREENSHOT_MODE) {
    return null;
  }

  return (
    <ExecuteReportAction
      onClick={handleExecute}
      loading={loading}
      isGreyedOut={!builderIsValid}
    />
  );
};

export default SignificanceExecute;
